import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import { format } from 'date-fns';
import {
  Plus, Search, Loader2, X, Trash2, MoreHorizontal, Pencil, CreditCard,
  TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, DollarSign, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v || 0);
const PAYMENT_METHODS = ['Cash', 'Credit Card', 'Debit Card', 'Bank Transfer', 'Venmo', 'Zelle', 'PayPal', 'Apple Pay', 'Other'];
const DEFAULT_CATEGORIES = ['Housing', 'Food', 'Transport', 'Utilities', 'Entertainment', 'Shopping', 'Health', 'Education', 'Insurance', 'Savings', 'Subscriptions', 'Personal', 'Other'];

function TransactionDialog({ open, onOpenChange, txn, companyId, onSave }) {
  const [form, setForm] = useState({
    description: '', amount: '', date: new Date().toISOString().slice(0, 10),
    type: 'expense', category: '', payment_method: '', notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (txn) {
        setForm({
          description: txn.description || '', amount: String(txn.amount || ''),
          date: txn.date || new Date().toISOString().slice(0, 10),
          type: txn.type || 'expense', category: txn.category || '',
          payment_method: txn.payment_method || '', notes: txn.notes || ''
        });
      } else {
        setForm({ description: '', amount: '', date: new Date().toISOString().slice(0, 10),
          type: 'expense', category: '', payment_method: '', notes: '' });
      }
    }
  }, [txn, open]);

  const handleSubmit = async () => {
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0, company_id: companyId };
      if (txn) {
        await axios.put(`${API}/api/transactions/${txn.id}`, payload, { withCredentials: true });
        toast.success('Transaction updated');
      } else {
        await axios.post(`${API}/api/transactions`, payload, { withCredentials: true });
        toast.success('Transaction added');
      }
      onSave(); onOpenChange(false);
    } catch { toast.error('Failed to save'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{txn ? 'Edit Transaction' : 'Add Transaction'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Button type="button" variant={form.type === 'expense' ? 'default' : 'outline'} size="sm"
              onClick={() => setForm(p => ({...p, type: 'expense'}))} className={form.type === 'expense' ? 'bg-red-600 hover:bg-red-700' : ''} data-testid="txn-type-expense">
              <ArrowDownRight className="w-3.5 h-3.5 mr-1" />Expense
            </Button>
            <Button type="button" variant={form.type === 'income' ? 'default' : 'outline'} size="sm"
              onClick={() => setForm(p => ({...p, type: 'income'}))} className={form.type === 'income' ? 'bg-blue-600 hover:bg-blue-700' : ''} data-testid="txn-type-income">
              <ArrowUpRight className="w-3.5 h-3.5 mr-1" />Income
            </Button>
          </div>
          <div>
            <Label>Description *</Label>
            <Input value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} placeholder="What was it for?" data-testid="txn-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Amount *</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={e => setForm(p => ({...p, amount: e.target.value}))} placeholder="0.00" data-testid="txn-amount-input" />
            </div>
            <div>
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="txn-date-btn">
                    {form.date || 'Pick date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[100]" align="start">
                  <Calendar mode="single" selected={form.date ? new Date(form.date + 'T12:00:00') : undefined}
                    onSelect={d => { if (d) setForm(p => ({...p, date: format(d, 'yyyy-MM-dd')})); }} />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={v => setForm(p => ({...p, category: v}))}>
                <SelectTrigger data-testid="txn-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {DEFAULT_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={form.payment_method} onValueChange={v => setForm(p => ({...p, payment_method: v}))}>
                <SelectTrigger data-testid="txn-payment-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Input value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} placeholder="Optional" data-testid="txn-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="save-txn-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : txn ? 'Update' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SpendingPage() {
  const { currentCompany } = useCompany();
  const [txns, setTxns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');
  const [addOpen, setAddOpen] = useState(false);
  const [editTxn, setEditTxn] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [activeView, setActiveView] = useState('register');

  const fetchData = useCallback(async () => {
    if (!currentCompany) return;
    setLoading(true);
    try {
      const [txnRes, sumRes] = await Promise.all([
        axios.get(`${API}/api/transactions`, { params: { company_id: currentCompany.id }, withCredentials: true }),
        axios.get(`${API}/api/transactions/summary`, { params: { company_id: currentCompany.id }, withCredentials: true })
      ]);
      setTxns(txnRes.data);
      setSummary(sumRes.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [currentCompany]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/api/transactions/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Deleted'); setDeleteTarget(null); fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleBulkDelete = async () => {
    try {
      await axios.post(`${API}/api/transactions/bulk-delete`, { ids: selected }, { withCredentials: true });
      toast.success(`Deleted ${selected.length} transactions`); setSelected([]); fetchData();
    } catch { toast.error('Failed'); }
  };

  const filtered = txns.filter(t => {
    if (typeFilter !== 'all' && t.type !== typeFilter) return false;
    if (catFilter !== 'all' && t.category !== catFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (t.description || '').toLowerCase().includes(q) || (t.category || '').toLowerCase().includes(q) || (t.notes || '').toLowerCase().includes(q);
    }
    return true;
  });

  const uniqueCats = [...new Set(txns.filter(t => t.category).map(t => t.category))];
  const toggleSelect = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAll = () => selected.length === filtered.length ? setSelected([]) : setSelected(filtered.map(t => t.id));

  if (!currentCompany) return null;
  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>;

  return (
    <div className="space-y-6" data-testid="spending-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Spending & Budget</h1>
          <p className="text-gray-500 text-sm mt-0.5">Track income, expenses, and budgets</p>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="add-txn-btn">
          <Plus className="w-4 h-4 mr-2" />Add Transaction
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Income</p>
                <p className="text-2xl font-bold text-blue-400 mt-1" data-testid="summary-income">{fmtCurrency(summary.total_income)}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Expenses</p>
                <p className="text-2xl font-bold text-red-400 mt-1" data-testid="summary-expenses">{fmtCurrency(summary.total_expenses)}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-400" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Net Cash Flow</p>
                <p className={`text-2xl font-bold mt-1 ${summary.net_cash_flow >= 0 ? 'text-blue-400' : 'text-red-400'}`} data-testid="summary-net">{fmtCurrency(summary.net_cash_flow)}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Tabs */}
      <Tabs value={activeView} onValueChange={setActiveView}>
        <TabsList className="bg-gray-100">
          <TabsTrigger value="register" className="data-[state=active]:bg-gray-700 text-gray-700" data-testid="tab-register">Register</TabsTrigger>
          <TabsTrigger value="budget" className="data-[state=active]:bg-gray-700 text-gray-700" data-testid="tab-budget">Budget</TabsTrigger>
          <TabsTrigger value="trends" className="data-[state=active]:bg-gray-700 text-gray-700" data-testid="tab-trends">Trends</TabsTrigger>
        </TabsList>
      </Tabs>

      {activeView === 'register' && (
        <>
          {/* Search & Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search transactions..."
                className="pl-9 bg-white border-gray-200 text-gray-900 placeholder:text-gray-600" data-testid="txn-search" />
            </div>
            <div className="flex gap-2">
              {['all', 'expense', 'income'].map(t => (
                <button key={t} onClick={() => setTypeFilter(t)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${typeFilter === t
                    ? t === 'expense' ? 'bg-red-600 text-white' : t === 'income' ? 'bg-blue-600 text-white' : 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                  data-testid={`type-filter-${t}`}>
                  {t === 'all' ? `All (${txns.length})` : t === 'expense' ? `Expenses (${txns.filter(x=>x.type==='expense').length})` : `Income (${txns.filter(x=>x.type==='income').length})`}
                </button>
              ))}
            </div>
          </div>

          {/* Category pills */}
          {uniqueCats.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {uniqueCats.map(cat => (
                <button key={cat} onClick={() => setCatFilter(catFilter === cat ? 'all' : cat)}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${catFilter === cat ? 'bg-gray-600 text-gray-900' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                  {cat}
                </button>
              ))}
              {(catFilter !== 'all' || search) && (
                <button onClick={() => { setCatFilter('all'); setSearch(''); }} className="px-3 py-1 rounded-full text-xs bg-red-50 text-red-400 hover:bg-red-900/50 flex items-center gap-1">
                  <X className="w-3 h-3" />Clear
                </button>
              )}
            </div>
          )}

          {/* Bulk Actions */}
          {selected.length > 0 && (
            <div className="flex items-center justify-between bg-rose-50 rounded-lg px-4 py-2.5 border border-rose-200">
              <span className="text-sm text-rose-700 font-medium">{selected.length} selected</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setBulkDeleteOpen(true)} className="text-red-400 border-red-800 hover:bg-red-50" data-testid="bulk-delete-txn-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setSelected([])} className="text-gray-500">Clear</Button>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-transparent">
                  <TableHead className="w-10 text-gray-500"><Checkbox checked={filtered.length > 0 && selected.length === filtered.length} onCheckedChange={toggleAll} /></TableHead>
                  <TableHead className="text-gray-500">Date</TableHead>
                  <TableHead className="text-gray-500">Description</TableHead>
                  <TableHead className="text-gray-500">Category</TableHead>
                  <TableHead className="text-gray-500 text-right">Amount</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow className="border-gray-200"><TableCell colSpan={6} className="text-center py-12 text-gray-600">
                    <CreditCard className="w-10 h-10 mx-auto mb-2" /><p className="font-medium">No transactions yet</p>
                  </TableCell></TableRow>
                ) : filtered.map(t => (
                  <TableRow key={t.id} className="border-gray-200 hover:bg-gray-100/50" data-testid={`txn-row-${t.id}`}>
                    <TableCell><Checkbox checked={selected.includes(t.id)} onCheckedChange={() => toggleSelect(t.id)} /></TableCell>
                    <TableCell className="text-sm text-gray-500 whitespace-nowrap">{t.date}</TableCell>
                    <TableCell>
                      <p className="text-sm font-medium text-gray-900">{t.description}</p>
                      {t.notes && <p className="text-xs text-gray-500 truncate max-w-[200px]">{t.notes}</p>}
                    </TableCell>
                    <TableCell>
                      {t.category ? <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{t.category}</span> : <span className="text-gray-600">-</span>}
                    </TableCell>
                    <TableCell className={`text-right font-semibold ${t.type === 'income' ? 'text-blue-400' : 'text-red-400'}`}>
                      {t.type === 'income' ? '+' : '-'}{fmtCurrency(t.amount)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-500"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditTxn(t)}><Pencil className="w-3.5 h-3.5 mr-2" />Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDeleteTarget(t)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex justify-end px-2"><span className="text-sm text-gray-500">{filtered.length} transaction{filtered.length !== 1 ? 's' : ''}</span></div>
        </>
      )}

      {activeView === 'budget' && summary && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Budget vs Actual (This Month)</h3>
          {summary.by_category?.length > 0 ? (
            <div className="space-y-3">
              {summary.by_category.map((cat, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">{cat.category}</span>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500">{fmtCurrency(cat.spent)}</span>
                      {cat.limit > 0 && <span className="text-gray-600">/ {fmtCurrency(cat.limit)}</span>}
                      {cat.over_budget && <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />}
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className={`h-2 rounded-full transition-all ${cat.over_budget ? 'bg-red-500' : cat.percentage > 80 ? 'bg-amber-500' : 'bg-blue-500'}`}
                      style={{ width: `${Math.min(cat.percentage || (cat.limit > 0 ? 0 : 50), 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-600 bg-white rounded-xl border border-gray-200">
              <CreditCard className="w-10 h-10 mx-auto mb-2" />
              <p>No spending data this month</p>
            </div>
          )}
        </div>
      )}

      {activeView === 'trends' && summary && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-medium text-gray-700 mb-4">Cash Flow (6 Months)</h3>
            {summary.trends?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={summary.trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="month" tick={{ fill: '#6B7280', fontSize: 11 }} tickFormatter={v => v?.slice(5)} />
                  <YAxis tick={{ fill: '#6B7280', fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => fmtCurrency(v)} contentStyle={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '8px', color: '#111827' }} />
                  <Bar dataKey="income" name="Income" fill="#3B82F6" radius={[4,4,0,0]} />
                  <Bar dataKey="expenses" name="Expenses" fill="#EF4444" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="text-center py-8 text-gray-600">No trend data yet</div>}
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-medium text-gray-700 mb-4">Net Cash Flow</h3>
            {summary.trends?.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={summary.trends}>
                  <defs>
                    <linearGradient id="netGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="month" tick={{ fill: '#6B7280', fontSize: 11 }} tickFormatter={v => v?.slice(5)} />
                  <YAxis tick={{ fill: '#6B7280', fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => fmtCurrency(v)} contentStyle={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '8px', color: '#111827' }} />
                  <Area type="monotone" dataKey="net" name="Net" stroke="#3B82F6" fill="url(#netGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <div className="text-center py-8 text-gray-600">No trend data yet</div>}
          </div>
        </div>
      )}

      {/* Dialogs */}
      <TransactionDialog open={addOpen || !!editTxn} onOpenChange={v => { if (!v) { setAddOpen(false); setEditTxn(null); } }}
        txn={editTxn} companyId={currentCompany?.id} onSave={fetchData} />

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Transaction</AlertDialogTitle>
            <AlertDialogDescription>Delete "{deleteTarget?.description}"?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete {selected.length} transactions?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setBulkDeleteOpen(false); handleBulkDelete(); }} className="bg-red-600 hover:bg-red-700">Delete All</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
